import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, Eye, Calendar as CalendarIcon, ClipboardList } from 'lucide-react';
import { RepairJob, JobStatus, Language } from '../types';
import { useTranslation } from '../LanguageContext';

interface BookingListProps {
  jobs: RepairJob[];
  onNavigateToJob: (job: RepairJob) => void;
}

export const BookingList: React.FC<BookingListProps> = ({ jobs, onNavigateToJob }) => {
  const { language } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const texts: Record<string, Record<Language, string>> = {
    search: { EN: 'Search customer, ID, or issue...', KH: 'ស្វែងរកឈ្មោះអតិថិជន លេខកូដ ឬបញ្ហា...', ZH: '搜索客户、ID 或问题...', JA: '顧客、ID、または問題を検索...', KO: '고객, ID 또는 문제 검색...' },
    id: { EN: 'Job ID', KH: 'លេខកូដ', ZH: '作业 ID', JA: 'ジョブ ID', KO: '작업 ID' },
    customer: { EN: 'Customer', KH: 'អតិថិជន', ZH: '客户', JA: '顧客', KO: '고객' },
    status: { EN: 'Status', KH: 'ស្ថានភាព', ZH: '状态', JA: 'ステータス', KO: '상태' },
    date: { EN: 'Date', KH: 'កាលបរិច្ឆេទ', ZH: '日期', JA: '日付', KO: '날짜' },
    action: { EN: 'Action', KH: 'សកម្មភាព', ZH: '操作', JA: '操作', KO: '작업' },
    all: { EN: 'All Status', KH: 'គ្រប់ស្ថានភាព', ZH: '所有状态', JA: 'すべてのステータス', KO: '모든 상태' },
    no_results: { EN: 'No matching repairs found.', KH: 'រកមិនឃើញការជួសជុលដែលសមស្របទេ។', ZH: '未找到匹配的维修。', JA: '該当する修理が見つかりません。', KO: '일치하는 수리 내역을 찾을 수 없습니다.' },
    view: { EN: 'View Case', KH: 'មើលករណី', ZH: '查看案例', JA: 'ケースを表示', KO: '사건 보기' }
  };

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = 
        job.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'ALL' || job.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [jobs, searchQuery, statusFilter]);

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusStyle = (status: JobStatus) => {
    switch (status) {
      case JobStatus.PENDING: return 'bg-amber-50 text-amber-600 border-amber-100';
      case JobStatus.CONFIRMED: return 'bg-blue-50 text-blue-600 border-blue-100';
      case JobStatus.IN_PROGRESS: return 'bg-violet-50 text-violet-600 border-violet-100';
      case JobStatus.COMPLETED: return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case JobStatus.REJECTED: return 'bg-red-50 text-red-600 border-red-100';
      default: return 'bg-slate-50 text-slate-400 border-slate-100';
    }
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder={texts.search[language]}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-12 pr-4 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-violet-50 outline-none font-bold transition-all"
          />
        </div>
        <div className="relative min-w-[200px]">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
            className="w-full pl-12 pr-8 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-violet-50 outline-none font-black text-[10px] uppercase tracking-widest appearance-none cursor-pointer"
          >
            <option value="ALL">{texts.all[language]}</option>
            {Object.values(JobStatus).map(status => (
              <option key={status} value={status}>{status.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-50">
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{texts.id[language]}</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{texts.customer[language]}</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{texts.status[language]}</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">{texts.date[language]}</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">{texts.action[language]}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {paginatedJobs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-8 py-24 text-center">
                    <div className="flex flex-col items-center opacity-20">
                      <ClipboardList size={48} className="mb-4" />
                      <p className="font-black uppercase text-xs tracking-widest">{texts.no_results[language]}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedJobs.map((job) => (
                  <tr key={job.id} className="group hover:bg-slate-50/50 transition-colors">
                    <td className="px-8 py-6">
                      <span className="font-mono text-xs font-black text-slate-400 group-hover:text-violet-600 transition-colors">#{job.id.slice(-6).toUpperCase()}</span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-black text-xs shadow-lg">
                          {job.customerName.charAt(0)}
                        </div>
                        <span className="font-bold text-slate-900">{job.customerName}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusStyle(job.status)}`}>
                        {job.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-2 text-slate-500 font-bold text-sm">
                        <CalendarIcon size={14} className="text-slate-300" />
                        {new Date(job.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-right">
                      <button
                        onClick={() => onNavigateToJob(job)}
                        className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:text-violet-600 hover:border-violet-200 hover:shadow-lg transition-all transform active:scale-95 flex items-center gap-2 ml-auto"
                      >
                        <Eye size={14} /> {texts.view[language]}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-8 py-6 bg-slate-50/30 border-t border-slate-50 flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Page <span className="text-slate-900">{currentPage}</span> of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-3 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-3 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 disabled:opacity-30 transition-all"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};